import { useEffect, useRef, useState } from 'react';
import { useT } from '@/store';

/**
 * Large, searchable driver-assignment control for a fleet card.
 *
 * A native <select> makes a fleet of thirty drivers a scroll-heavy
 * alphabetical list with no way to jump to a name; this filters as the
 * dispatcher types and shows who is currently assigned as the closed state,
 * which is the thing a card gets scanned for. Disabled entirely for an
 * archived vehicle — same reason the old <select> was disabled there: the
 * backend 404s the assign-driver route for one, and a control that looks
 * live but always fails is worse than one that looks inert.
 */
export function DriverAssignField({ vehicle, drivers, disabled, onAssign }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const current = drivers.find((d) => d.driver_id === vehicle.current_driver_id);
  const filtered = query
    ? drivers.filter((d) => d.full_name.toLowerCase().includes(query.toLowerCase()))
    : drivers;

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const onDocPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(driverId) {
    onAssign(driverId);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="field flex w-full items-center gap-2 !py-2 text-left disabled:cursor-default disabled:opacity-50"
      >
        <span
          aria-hidden="true"
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase ${
            current ? 'bg-accent/20 text-accent-hi' : 'bg-white/6 text-dim'
          }`}
        >
          {current ? current.full_name.slice(0, 1) : '⌕'}
        </span>
        <span className={`flex-1 truncate text-[12px] ${current ? 'text-ink' : 'text-dim'}`}>
          {current ? current.full_name : t('transportas.searchOrAssignDriver')}
        </span>
        {!disabled ? (
          <span aria-hidden="true" className="text-dim">
            ⌄
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 overflow-hidden rounded-[12px] border border-line bg-panel-2 shadow-xl">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('transportas.searchDriverPh')}
            className="field !rounded-none !border-x-0 !border-t-0 !text-[11px]"
          />
          <ul className="max-h-44 overflow-y-auto py-1" role="listbox">
            <li>
              <button
                type="button"
                onClick={() => pick('')}
                className="flex w-full items-center px-3 py-1.5 text-left text-[11.5px] text-dim hover:bg-white/6"
              >
                {t('transportas.unassigned')}
              </button>
            </li>
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[11.5px] text-dim">{t('common.noMatches')}</li>
            ) : (
              filtered.map((d) => (
                <li key={d.driver_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={d.driver_id === vehicle.current_driver_id}
                    onClick={() => pick(d.driver_id)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11.5px] hover:bg-white/6 ${
                      d.driver_id === vehicle.current_driver_id ? 'text-accent-hi' : 'text-ink'
                    }`}
                  >
                    {d.full_name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
